// frontend/src/components/projects/ProjectInvitesGrid.tsx
import React, { useMemo } from "react";
import { Alert, Card, Group, Stack, Text } from "@mantine/core";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-enterprise";

import { useAppSelector } from "../app/hooks";
import { selectProjectNo } from "../globalSelectors";
import type { IProjectInvite, IProjectInviteStatus } from "@multiconsult-group/multiplacer-core";
import { selectHttpProject } from "../projectSelectors";
import { formatShortDate } from "../globalUtils";
import { selectIsSystemAdmin } from "../userSelectors";

type InviteRow = {
  email: string;
  status: IProjectInviteStatus;
  createdAt: string;
  acceptedAt: string;
};

function statusLabel(status: IProjectInviteStatus): string {
  switch (status) {
    case "PENDING":
      return "Venter";
    case "ACCEPTED":
      return "Akseptert";
    case "REVOKED":
      return "Tilbakekalt";
    default:
      return status;
  }
}

export const ProjectInvitesGrid: React.FC = () => {
  const projectNo = useAppSelector(selectProjectNo);
  const isSystemAdmin = useAppSelector(selectIsSystemAdmin);
  const project = useAppSelector(selectHttpProject);

  if (!project) return null;

  if (!isSystemAdmin) {
    return (
      <Alert color="yellow" title="Begrenset tilgang">
        Kun systemadministratorer kan se invitasjoner.
      </Alert>
    );
  }

  const rowData: InviteRow[] = useMemo(() => {
    const invites = project.invites as IProjectInvite[] | undefined;
    if (!Array.isArray(invites)) return [];

    return invites.map((inv) => ({
      email: inv.email,
      status: inv.status,
      createdAt: inv.createdAt ? formatShortDate(inv.createdAt) : "",
      acceptedAt: inv.acceptedAt ? formatShortDate(inv.acceptedAt) : "",
    }));
  }, [project.invites]);

  const columnDefs = useMemo<ColDef<InviteRow>[]>(
    () => [
      { field: "email", headerName: "E-post", flex: 1, minWidth: 260 },
      {
        field: "status",
        headerName: "Status",
        width: 160,
        valueFormatter: (p) => statusLabel(p.value as IProjectInviteStatus),
      },
      { field: "createdAt", headerName: "Opprettet", width: 170 },
      { field: "acceptedAt", headerName: "Akseptert", width: 170 },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: false,
      filter: false,
      floatingFilter: false,
    }),
    []
  );

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text fw={600}>Invitasjoner</Text>
          <Text size="sm" c="dimmed">
            Prosjekt {projectNo}
          </Text>
        </Group>

        <div style={{ width: "100%", height: 220 }}>
          <AgGridReact<InviteRow>
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            suppressCellFocus
            headerHeight={36}
            rowHeight={36}
            domLayout="autoHeight"
          />
        </div>
      </Stack>
    </Card>
  );
};
